<template>
  <div class="position-relative" @mouseleave="hideMenu">
    <button
      @click="handleClick"
      @mouseenter="showMenu"
      :class="buttonClasses"
      :key="orderState"
      :disabled="disabled"
      v-tooltip="tooltipText"
      data-tooltip-position="top">
      <i :class="iconClasses"></i>
    </button>


    <!-- Kontextové menu zůstává stejné -->
    <div
      v-if="menuVisible && hasMenu && !disabled"
      class="dropdown-menu show position-absolute"
      style="top: 100%; right: 0; z-index: 1050; min-width: 200px;">

      <template v-if="!version.order_confirmed && version.order_number">
        <button class="dropdown-item" @click="showOrder" type="button">
          <i class="bi-eye me-2"></i>
          Zobrazit objednávku
        </button>
        <div class="dropdown-divider"></div>
        <button class="dropdown-item" @click="confirmOrder" type="button">
          <i class="bi-check-circle me-2"></i>
          Potvrdit objednávku
        </button>
        <button class="dropdown-item" @click="editOrder" type="button">
          <i class="bi-pencil me-2"></i>
          Upravit objednávku
        </button>
        <div class="dropdown-divider"></div>
        <button class="dropdown-item text-danger" @click="cancelOrder" type="button">
          <i class="bi-trash me-2"></i>
          Zrušit objednávku
        </button>
      </template>

      <template v-if="version.order_confirmed">
        <button class="dropdown-item" @click="showOrder" type="button">
          <i class="bi-eye me-2"></i>
          Zobrazit objednávku
        </button>
        <div class="dropdown-divider"></div>
        <button class="dropdown-item text-danger" @click="unconfirmOrder" type="button">
          <i class="bi-x-circle me-2"></i>
          Zrušit potvrzení objednávky
        </button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref } from 'vue'

const props = defineProps({
  version: {
    type: Object,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  showMenuDelay: {
    type: Number,
    default: 700
  },
  hideMenuDelay: {
    type: Number,
    default: 350
  }
})

const emit = defineEmits([
  'new-order',
  'confirm-order',
  'edit-order',
  'cancel-order',
  'unconfirm-order',
  'show-order'
])

const menuVisible = ref(false)
let hideTimeout = null
let showTimeout = null

// Všechny computed properties zůstávají stejné
const orderState = computed(() => {
  if (!props.version.order_number) return 'no-order'
  if (!props.version.order_confirmed) return 'unconfirmed'
  return 'confirmed'
})

const buttonClasses = computed(() => {
  const baseClasses = 'btn btn-sm'

  if (props.disabled) {
    return `${baseClasses} btn-secondary disabled`
  }

  switch (orderState.value) {
    case 'no-order':
      return `${baseClasses} btn-outline-primary`
    case 'unconfirmed':
      return `${baseClasses} btn-outline-dark`
    case 'confirmed':
      return `${baseClasses} btn-outline-success`
    default:
      return `${baseClasses} btn-outline-primary`
  }
})

const iconClasses = computed(() => {
  switch (orderState.value) {
    case 'no-order':
      return 'bi-bag'
    case 'unconfirmed':
      return 'bi-bag-fill'
    case 'confirmed':
      return 'bi-bag-check-fill'
    default:
      return 'bi-bag'
  }
})

const tooltipText = computed(() => {
  if (props.disabled) {
    return 'Tlačítko je deaktivováno'
  }

  switch (orderState.value) {
    case 'no-order':
      return 'Vytvoř novou objednávku'
    case 'unconfirmed':
      return 'Vytvořená objednávka'
    case 'confirmed':
      return 'Potvrzená objednávka'
    default:
      return 'Objednávka'
  }
})

const hasMenu = computed(() => {
  return (orderState.value === 'unconfirmed' || orderState.value === 'confirmed') && !props.disabled
})

// Všechny event handlers zůstávají stejné
const handleClick = () => {
  if (props.disabled) return

  if (orderState.value === 'no-order') {
    emit('new-order', props.version.id)
  }
}

const showMenu = () => {
  if (props.disabled) return

  if (hasMenu.value) {
    clearTimeout(hideTimeout)
    clearTimeout(showTimeout)

    showTimeout = setTimeout(() => {
      menuVisible.value = true
    }, props.showMenuDelay)
  }
}

const hideMenu = () => {
  if (props.disabled) return

  clearTimeout(showTimeout)

  hideTimeout = setTimeout(() => {
    menuVisible.value = false
  }, props.hideMenuDelay)
}

const showOrder = () => {
  if (props.disabled) return
  emit('show-order', props.version.id)
  menuVisible.value = false
}

const confirmOrder = () => {
  if (props.disabled) return
  emit('confirm-order', props.version.id)
  menuVisible.value = false
}

const editOrder = () => {
  if (props.disabled) return
  emit('edit-order', props.version.id)
  menuVisible.value = false
}

const cancelOrder = () => {
  if (props.disabled) return
  emit('cancel-order', props.version.id)
  menuVisible.value = false
}

const unconfirmOrder = () => {
  if (props.disabled) return
  emit('unconfirm-order', props.version.id)
  menuVisible.value = false
}
</script>

<style scoped>
.dropdown-menu {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
  border: 1px solid rgba(0, 0, 0, 0.175);
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.text-danger:hover {
  background-color: #f8d7da;
  color: #721c24;
}

.dropdown-item.text-warning:hover {
  background-color: #fff3cd;
  color: #856404;
}

.btn.disabled {
  pointer-events: none;
  opacity: 0.65;
}

</style>